import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import './Header.css';

const SECTIONS = ['home', 'about', 'mission', 'courses', 'contact'];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const { theme, toggleTheme } = useTheme();

  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';
  const isBlogPath = location.pathname.startsWith('/blog');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);

      if (!isHome) return;

      const scrollPosition = window.scrollY + 150;
      for (let i = SECTIONS.length - 1; i >= 0; i--) {
        const section = document.getElementById(SECTIONS[i]);
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(SECTIONS[i]);
          return;
        }
      }
      setActiveSection('home');
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHome, location.pathname]);

  const scrollToSection = useCallback(
    (sectionId) => {
      setIsMobileMenuOpen(false);

      if (!isHome) {
        navigate('/', { state: { scrollTo: sectionId } });
        return;
      }

      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    },
    [isHome, navigate]
  );

  // When we land on home after clicking an anchor from another route,
  // pick up the requested section from the router state.
  useEffect(() => {
    if (!isHome) return;
    const scrollTo = location.state?.scrollTo;
    if (!scrollTo) return;
    const timer = setTimeout(() => {
      const el = document.getElementById(scrollTo);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 50);
    return () => clearTimeout(timer);
  }, [isHome, location.state]);

  const goToBlog = () => {
    setIsMobileMenuOpen(false);
    navigate('/blog');
  };

  const isAnchorActive = (id) => isHome && activeSection === id && !isBlogPath;

  return (
    <header className={`header ${isScrolled ? 'scrolled' : ''}`}>
      <div className="header-container">
        <nav className={`nav ${isMobileMenuOpen ? 'nav-open' : ''}`}>
          <a
            href="#home"
            onClick={(e) => { e.preventDefault(); scrollToSection('home'); }}
            className={isAnchorActive('home') ? 'active' : ''}
          >
            <span>Home</span>
          </a>
          <a
            href="#about"
            onClick={(e) => { e.preventDefault(); scrollToSection('about'); }}
            className={isAnchorActive('about') ? 'active' : ''}
          >
            <span>About</span>
          </a>
          <a
            href="#mission"
            onClick={(e) => { e.preventDefault(); scrollToSection('mission'); }}
            className={isAnchorActive('mission') ? 'active' : ''}
          >
            <span>Mission</span>
          </a>
          <a
            href="#courses"
            onClick={(e) => { e.preventDefault(); scrollToSection('courses'); }}
            className={isAnchorActive('courses') ? 'active' : ''}
          >
            <span>Courses</span>
          </a>
          <a
            href="/blog"
            onClick={(e) => { e.preventDefault(); goToBlog(); }}
            className={isBlogPath ? 'active' : ''}
          >
            <span>Blog</span>
          </a>
          <a
            href="#contact"
            onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }}
            className={isAnchorActive('contact') ? 'active' : ''}
          >
            <span>Contact</span>
          </a>
        </nav>

        <button
          className="theme-toggle"
          onClick={toggleTheme}
          aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          <span className="theme-toggle-track">
            <span className="theme-toggle-thumb">
              {theme === 'dark' ? (
                <svg className="theme-toggle-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                </svg>
              ) : (
                <svg className="theme-toggle-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="4" />
                  <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
                </svg>
              )}
            </span>
          </span>
        </button>

        <button
          className="mobile-menu-toggle"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>
    </header>
  );
};

export default Header;
